<script>
import { useAlert } from 'dashboard/composables';
import { mapActions } from 'vuex';
import { apiErrorCode, apiErrorMessage } from '../../../../api/coLiving/errors';
import { CARD_CLASS } from '../constants';
import { visitButtons } from '../ticketHelpers';
import BookingModal from './BookingModal.vue';
import VisitReasonModal from './VisitReasonModal.vue';

// A visit is always with the ticket's person in charge. The buttons follow the
// visit's status: an open offer can be confirmed for the tenant (agreed by
// phone) or withdrawn; a booked visit can be rescheduled, cancelled,
// completed or marked no-show (only once its start time has passed).
export default {
  name: 'VisitsPanel',
  components: { BookingModal, VisitReasonModal },
  props: {
    ticket: { type: Object, required: true },
    canEdit: { type: Boolean, default: false },
  },
  emits: ['changed'],
  data() {
    return {
      cardClass: CARD_CLASS,
      bookingFor: undefined,
      reasonFor: null,
      reasonError: '',
      isBusy: false,
    };
  },
  computed: {
    visits() {
      return this.ticket.visits || [];
    },
    // New visits are arranged while the ticket waits for a slot, or booked as
    // a further visit while one is already scheduled.
    canArrange() {
      return (
        this.canEdit &&
        ['AWAITING_SLOT', 'SCHEDULED'].includes(this.ticket.status)
      );
    },
  },
  methods: {
    ...mapActions('coLivingTickets', ['visitAction']),
    buttons(visit) {
      return this.canEdit ? visitButtons(visit) : [];
    },
    // Runs one visit call; success refreshes the ticket through the parent.
    async run(visit, action, payload) {
      this.isBusy = true;
      try {
        await this.visitAction({
          ticketId: this.ticket.id,
          visitId: visit.id,
          action,
          payload,
        });
        useAlert(this.$t('CO_LIVING.TICKETS.BOOKING.DONE'));
        this.$emit('changed');
        return true;
      } catch (err) {
        const code = apiErrorCode(err);
        const message =
          code === 'visit_not_started'
            ? this.$t('CO_LIVING.TICKETS.BOOKING.NOT_STARTED')
            : apiErrorMessage(this, err, 'CO_LIVING.TICKETS.BOOKING.FAILED');
        if (this.reasonFor) {
          this.reasonError = message;
        } else {
          useAlert(message);
        }
        // The visit moved on (taken by someone else, no longer open): the
        // buttons are stale.
        if (
          ['no_open_offer', 'visit_not_booked', 'slot_unavailable'].includes(
            code
          )
        ) {
          this.$emit('changed');
        }
        return false;
      } finally {
        this.isBusy = false;
      }
    },
    onButton(visit, button, option) {
      if (button === 'confirm') return this.run(visit, 'confirm', { option });
      if (button === 'complete') return this.run(visit, 'complete');
      if (button === 'reschedule') {
        this.bookingFor = visit;
        return undefined;
      }
      this.reasonError = '';
      this.reasonFor = {
        visit,
        action: button === 'no_show' ? 'noShow' : button,
      };
      return undefined;
    },
    async confirmReason(reason) {
      const { visit, action } = this.reasonFor;
      if (await this.run(visit, action, reason ? { reason } : {})) {
        this.reasonFor = null;
      }
    },
  },
};
</script>

<template>
  <section :class="cardClass" data-testid="co-living-visits">
    <div class="flex items-start justify-between gap-4">
      <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100">
        {{ $t('CO_LIVING.TICKETS.VISITS.TITLE') }}
      </h3>
      <woot-button
        v-if="canArrange"
        icon="add"
        size="small"
        data-testid="co-living-arrange-visit"
        @click="bookingFor = null"
      >
        {{ $t('CO_LIVING.TICKETS.VISITS.ARRANGE') }}
      </woot-button>
    </div>

    <p
      v-if="!visits.length"
      class="mt-3 text-sm text-slate-500 dark:text-slate-400"
      data-testid="co-living-visits-empty"
    >
      {{ $t('CO_LIVING.TICKETS.VISITS.EMPTY') }}
    </p>
    <ul v-else class="mt-3 divide-y divide-slate-200 dark:divide-slate-700">
      <li
        v-for="visit in visits"
        :key="visit.id"
        class="py-3"
        :data-testid="`co-living-visit-${visit.id}`"
      >
        <div
          class="flex flex-wrap items-center gap-2 text-sm text-slate-900 dark:text-slate-100"
        >
          <span class="font-medium">
            {{ $t('CO_LIVING.TICKETS.VISITS.NUMBER', { n: visit.visit_no }) }}
          </span>
          <span
            class="px-2 py-0.5 text-xs font-medium rounded bg-slate-100 text-slate-700 dark:bg-slate-600 dark:text-slate-200"
            :data-testid="`co-living-visit-status-${visit.id}`"
          >
            {{ visit.status }}
          </span>
          <span v-if="visit.label">{{ visit.label }}</span>
          <span class="text-slate-500">
            {{
              $t('CO_LIVING.TICKETS.VISITS.SUMMARY', {
                purpose: visit.purpose,
                minutes: visit.minutes,
              })
            }}
            <template v-if="visit.member">
              {{
                $t('CO_LIVING.TICKETS.VISITS.WITH', { name: visit.member.name })
              }}
            </template>
          </span>
          <span
            v-if="visit.flag === 'needs_reschedule'"
            class="px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-300"
            :data-testid="`co-living-visit-flag-${visit.id}`"
          >
            {{ $t('CO_LIVING.TICKETS.VISITS.NEEDS_RESCHEDULE') }}
          </span>
        </div>

        <ul
          v-if="visit.status === 'Offered'"
          class="mt-2 flex flex-col gap-1 text-sm"
        >
          <li
            v-for="option in visit.offered"
            :key="option.option"
            class="flex items-center gap-2 text-slate-700 dark:text-slate-200"
          >
            {{ $t('CO_LIVING.TICKETS.VISITS.OPTION', { n: option.option }) }}:
            {{ option.label }}
            <woot-button
              v-if="canEdit"
              size="small"
              variant="clear"
              :disabled="isBusy"
              :data-testid="`co-living-visit-confirm-${visit.id}-${option.option}`"
              @click="onButton(visit, 'confirm', option.option)"
            >
              {{ $t('CO_LIVING.TICKETS.VISIT_ACTIONS.CONFIRM') }}
            </woot-button>
          </li>
          <li class="text-xs text-slate-500">
            {{
              $t('CO_LIVING.TICKETS.VISITS.EXPIRES', { date: visit.expires_on })
            }}
          </li>
        </ul>

        <p
          v-if="visit.reason"
          class="mt-1 text-xs text-slate-500 dark:text-slate-400"
        >
          {{ visit.reason }}
        </p>

        <div v-if="buttons(visit).length" class="flex flex-wrap gap-2 mt-2">
          <woot-button
            v-for="button in buttons(visit).filter(b => b !== 'confirm')"
            :key="button"
            size="small"
            color-scheme="secondary"
            :disabled="isBusy"
            :data-testid="`co-living-visit-${button}-${visit.id}`"
            @click="onButton(visit, button)"
          >
            {{ $t(`CO_LIVING.TICKETS.VISIT_ACTIONS.${button.toUpperCase()}`) }}
          </woot-button>
        </div>
      </li>
    </ul>

    <BookingModal
      v-if="bookingFor !== undefined"
      :ticket="ticket"
      :visit="bookingFor"
      @close="bookingFor = undefined"
      @done="$emit('changed')"
    />
    <VisitReasonModal
      v-if="reasonFor"
      :action="reasonFor.action"
      :error="reasonError"
      :is-loading="isBusy"
      @close="reasonFor = null"
      @confirm="confirmReason"
    />
  </section>
</template>
